import { t } from 'i18next';
import {
  IOrderItem,
  IMilestone,
  IConsumptionBag,
  ChoicesOrderUnitMeasure,
  IConsumptionBagTransformed,
} from '../types';
import { ILegalEntityItem } from '@/types/legal-entity';
import { IBagAllocation, ChoicesStatusSalesOrder, IConsumptionBagAllocation } from '@/types/order';
import { PaymentType, ChoicesOrderItemType } from '../types/order-types';
import { ISalesOrder, ChoicesOrderItemLabel } from '@/types';
import { getAllOrderItems, transformConsumptionBags } from '../hooks';

/**
 * Interface for transformed sales data used in form initialization
 */
export interface ITransformedSalesData {
  legalEntities: ILegalEntityItemForm[];
  salesOrders: Record<string, any>;
  unitMeasure: Record<string, ChoicesOrderUnitMeasure>;
}

/**
 * Interface for transformed bag allocations data used in form initialization
 */
export interface ITransformedBagAllocationsData {
  selectedConsumptionBags: IConsumptionBagTransformed[];
  consumptionOrders: Record<string, IFormConsumptionOrder>;
}

/**
 * Interface for transformed sales order item
 */
export interface ITransformedSalesOrderItem {
  orderItem: string;
  unitMeasure: ChoicesOrderUnitMeasure;
  value: number;
  amount: number;
  itemId?: string;
  selectedBag?: string | null;
}

/**
 * Interface for transformed sales order
 */
export interface ITransformedSalesOrder {
  _id: string;
  label: string;
  legal_name: string;
  tax_number: string;
  legalEntity: ILegalEntityItem;
  order: string;
  documents: string | null;
  OVNumber?: string | null;
  totalAmount: number;
  status: string;
  salesOrdersItems: ITransformedSalesOrderItem[];
  items: ITransformedSalesOrderItem[];
}

export interface ILegalEntityItemForm extends ILegalEntityItem {
  label: string;
}

/**
 * Interface for consumption order from form data
 */
export interface IFormConsumptionOrder {
  totalAmount: number;
  consumptionBag: IConsumptionBagTransformed;
  consumptionOrderItems: IFormConsumptionOrderItemsMap;
}

export const unitMesureChoices = [
  { value: ChoicesOrderUnitMeasure.quantity, label: t('Quantity') },
  {
    value: ChoicesOrderUnitMeasure.percentageAmount,
    label: t('Percentage of the amount'),
  },
  { value: ChoicesOrderUnitMeasure.amount, label: t('Amount') },
];

export const unitMesureChoicesHelpText: Record<ChoicesOrderUnitMeasure, string> = {
  [ChoicesOrderUnitMeasure.quantity]: t('Enter the quantity to be billed'),
  [ChoicesOrderUnitMeasure.percentageAmount]: t('Enter the percentage to be billed'),
  [ChoicesOrderUnitMeasure.amount]: t('Enter the amount to be billed'),
};

export interface IFormConsumptionBag {
  _id: string;
  sku: ConsumptionBagSku;
  organization: ConsumptionBagOrganization;
  plan: string;
  baseAmount: number;
  initialDate: Date;
  expirationDate: Date;
  status: string;
  createdAt: Date;
  updatedAt: Date;
  currentBalance: number;
  totalMovements: number;
}

export interface ConsumptionBagOrganization {
  _id: string;
  name: string;
}

export interface ConsumptionBagSku {
  _id: string;
  name: string;
  product: string;
  partner: ConsumptionBagPartner;
}

export interface ConsumptionBagPartner {
  _id: string;
  name: string;
  logo: ConsumptionBagLogo;
}

export interface ConsumptionBagLogo {
  name: string;
  size: number;
  type: string;
  url: string;
}

/**
 * Interface for current item price data comparison
 */
interface ICurrentItemPriceData {
  price: number;
  finalPrice: number;
}

/**
 * Interface for current item billing milestone data comparison
 */
interface ICurrentItemMilestoneData {
  customizeBillingMilestones: boolean;
  customMilestonesLength: number;
  finalCustomMilestonesLength: number;
}

/**
 * Interface for order data structure used in milestone comparison
 */
interface IOrderDataForMilestoneComparison {
  items?: IOrderItem[];
  itemsToAddLicenses?: IOrderItem[];
  itemsToRenewal?: IOrderItem[];
  itemsToUpgrade?: IOrderItem[];
}

/**
 * Interface for a single consumption order item in the form
 */
export interface IFormConsumptionOrderItem {
  value: number;
  amount: number;
  unitMeasure?: ChoicesOrderUnitMeasure;
}

/**
 * Interface for a map of order item IDs to their consumption order item data
 */
export type IFormConsumptionOrderItemsMap = Record<string, IFormConsumptionOrderItem>;

/**
 * Gets all milestones from an order item
 * Prioritizes custom billing milestones over default provision milestones
 *
 * @param item - The order item to get milestones from
 * @returns Array of milestone objects or empty array if none found
 */
export const getItemMilestones = (item: IOrderItem) => {
  if (!item) {
    return [];
  }

  if (item.customizeBillingMilestones) {
    if (item.customMilestones?.length > 0) return item.customMilestones;
    if (item.finalCustomMilestones?.length > 0) return item.finalCustomMilestones;
  }

  return item.summary?.provision?.milestones || [];
};

/**
 * Calculates subtotal, total and discount amounts from all milestones of an order item
 * Prioritizes custom billing milestones over default provision milestones
 * Uses a single iteration for optimal performance
 *
 * @param item - The order item to calculate milestones amounts from
 * @returns Object containing subtotal, total and discount amounts rounded to 2 decimal places
 */
export const calculateMilestonesTotal = (
  item: IOrderItem
): { subtotal: number; total: number; discount: number } => {
  const milestones = getItemMilestones(item);

  if (!milestones || milestones.length === 0) {
    return { subtotal: 0, total: 0, discount: 0 };
  }

  const totals = milestones.reduce(
    (
      acc: { subtotal: number; total: number; discount: number },
      milestone: IMilestone & { amount: number }
    ) => {
      acc.subtotal += milestone?.subtotal || milestone?.amount || 0;
      acc.total += milestone?.total || milestone?.amount || 0;
      acc.discount += milestone?.totalDiscount || 0;
      return acc;
    },
    { subtotal: 0, total: 0, discount: 0 }
  );

  return {
    subtotal: totals.subtotal,
    total: totals.total,
    discount: totals.discount,
  };
};

/**
 * Gets the current milestone for an order item
 * Prioritizes custom billing milestones over default provision milestones
 *
 * @param item - The order item to get milestone from
 * @returns The current milestone object or undefined if none found
 */
export const getCurrentMilestone = (item: IOrderItem) => {
  if (!item) {
    return undefined;
  }

  if (item.customizeBillingMilestones) {
    if (item.customizeBillingMilestones) {
      if (item.customMilestones?.[0]) return item.customMilestones?.[0];
      if (item.finalCustomMilestones?.[0]) return item.finalCustomMilestones?.[0];
    }
  }

  return item.summary?.provision?.milestones?.[0];
};

/**
 * Gets the total amount from the current milestone
 *
 * @param item - The order item to get milestone total from
 * @returns The milestone total amount rounded to 2 decimal places, or 0 if not found
 */
export const getCurrentMilestoneTotal = (item: IOrderItem): number => {
  const currentMilestone = getCurrentMilestone(item);
  return currentMilestone?.total ?? currentMilestone?.amount ?? 0;
};

/**
 * Transforms form salesOrders object into API-ready array format
 *
 * @param formSalesOrders - Sales orders object from form
 * @param legalEntities - Array of legal entities for reference data
 * @param orderId - Current order ID
 * @returns Array of transformed sales orders ready for API submission
 */
export const transformSalesOrders = (
  formSalesOrders: Record<string, any>,
  legalEntities: ILegalEntityItem[],
  orderId: string
): ITransformedSalesOrder[] => {
  if (!formSalesOrders || typeof formSalesOrders !== 'object') {
    return [];
  }

  return Object.entries(formSalesOrders).map(([legalEntityId, salesOrderData]) => {
    const legalEntity = legalEntities.find((entity) => entity._id === legalEntityId);

    if (!legalEntity) {
      throw new Error(`Legal entity not found for ID: ${legalEntityId}`);
    }

    const salesOrdersItems: ITransformedSalesOrderItem[] = [];

    if (salesOrderData.salesOrderItems) {
      Object.entries(salesOrderData.salesOrderItems).forEach(
        ([itemId, itemData]: [string, any]) => {
          salesOrdersItems.push({
            orderItem: itemId,
            unitMeasure: itemData.unitMeasure || ChoicesOrderUnitMeasure.quantity,
            value: itemData.value || 0,
            amount: itemData.amount || 0,
            selectedBag: itemData.selectedBag || null,
            itemId,
          });
        }
      );
    }

    const transformedSalesOrder: ITransformedSalesOrder = {
      _id: salesOrderData._id || legalEntityId,
      label: legalEntity.legal_name,
      legal_name: legalEntity.legal_name,
      tax_number: legalEntity.tax_number,
      legalEntity: legalEntity,
      order: orderId,
      documents: salesOrderData.documents || [],
      OVNumber: salesOrderData.OVNumber || null,
      totalAmount: salesOrderData.totalAmount || 0,
      status: salesOrderData.status || ChoicesStatusSalesOrder.active,
      items: salesOrdersItems,
      salesOrdersItems,
    };

    return transformedSalesOrder;
  });
};

/**
 * Transforms consumption orders from form data into API-ready format
 * Converts form consumption orders object into ConsumptionBagAllocationDto array structure
 *
 * @param consumptionOrders - Consumption orders object from form
 * @param consumptionBags - Array of consumption bags for reference data
 * @param formUnitMeasure - Unit measure mapping by item ID
 * @returns Array of consumption bag allocations ready for API submission
 */
export const transformConsumptionOrders = (
  consumptionOrders: Record<string, IFormConsumptionOrder>,
  formUnitMeasure: Record<string, ChoicesOrderUnitMeasure>
): IConsumptionBagAllocation[] => {
  if (!consumptionOrders || typeof consumptionOrders !== 'object') {
    return [];
  }

  return Object.entries(consumptionOrders).map(
    ([bagId, consumptionOrder]: [string, IFormConsumptionOrder]): IConsumptionBagAllocation => {
      const consumptionBagId = consumptionOrder.consumptionBag?._id || bagId;

      const transformedItems: IFormConsumptionOrderItemsMap = {};

      if (
        consumptionOrder.consumptionOrderItems &&
        typeof consumptionOrder.consumptionOrderItems === 'object'
      ) {
        Object.entries(consumptionOrder.consumptionOrderItems).forEach(([itemId, itemData]) => {
          if (itemData && typeof itemData === 'object') {
            transformedItems[itemId] = {
              value: Number(itemData.value),
              amount: Number(itemData.amount),
              unitMeasure: formUnitMeasure[itemId],
            };
          }
        });
      }

      let totalAmount = Number(consumptionOrder.totalAmount) || 0;
      if (totalAmount <= 0) {
        totalAmount = Object.values(transformedItems).reduce(
          (sum, item) => sum + (item.amount || 0),
          0
        );
      }

      return {
        consumptionOrderItems: transformedItems,
        totalAmount: Number(totalAmount.toFixed(2)),
        consumptionBagId,
      };
    }
  );
};

/**
 * Transforms currentSalesOrders array into form structure for defaultValues
 * Excludes items that are not present in orderItemsToBill
 * Initializes new items with default values if they exist in orderItemsToBill but not in sales orders
 *
 * @param currentSalesOrders - Array of sales orders from API
 * @param orderItemsToBill - Array of items that should be billed
 * @returns Object with legalEntities and salesOrders for form initialization
 */
export const transformCurrentSalesOrdersToFormData = (
  currentSalesOrders: ISalesOrder[],
  orderItemsToBill: IOrderItem[]
): ITransformedSalesData => {
  if (
    !currentSalesOrders ||
    !Array.isArray(currentSalesOrders) ||
    currentSalesOrders.length === 0
  ) {
    return {
      legalEntities: [],
      salesOrders: {},
      unitMeasure: {},
    };
  }

  const validItemIds = new Set((orderItemsToBill || []).map((item) => item._id).filter(Boolean));
  const legalEntities: ILegalEntityItemForm[] = [];
  const salesOrders: Record<string, any> = {};
  const unitMeasure: Record<string, ChoicesOrderUnitMeasure> = {};

  const processedItemIds = new Set<string>();

  currentSalesOrders.forEach((salesOrder) => {
    const legalEntityId = salesOrder.legalEntity._id;

    if (!legalEntities.find((entity) => entity._id === legalEntityId)) {
      legalEntities.push({
        _id: salesOrder.legalEntity._id,
        label: salesOrder.legalEntity.legal_name,
        legal_name: salesOrder.legalEntity.legal_name,
        tax_number: salesOrder.legalEntity.tax_number,
        city: salesOrder.legalEntity.city || '',
        address: salesOrder.legalEntity.address || '',
      } as ILegalEntityItemForm);
    }

    const salesOrderItems: Record<string, any> = {};

    if (salesOrder.salesOrdersItems && Array.isArray(salesOrder.salesOrdersItems)) {
      salesOrder.salesOrdersItems.forEach((item) => {
        const itemId = item.orderItem;

        if (validItemIds.has(itemId)) {
          salesOrderItems[itemId] = {
            unitMeasure: item.unitMeasure || ChoicesOrderUnitMeasure.quantity,
            value: item.value || 0,
            amount: item.amount || 0,
            selectedBag: item.selectedBag || null,
          };

          unitMeasure[itemId] =
            (item.unitMeasure as ChoicesOrderUnitMeasure) || ChoicesOrderUnitMeasure.quantity;

          processedItemIds.add(itemId);
        }
      });
    }

    orderItemsToBill.forEach((orderItem) => {
      const itemId = orderItem._id;

      if (validItemIds.has(itemId) && !salesOrderItems[itemId]) {
        salesOrderItems[itemId] = {
          unitMeasure: ChoicesOrderUnitMeasure.quantity,
          value: 0,
          amount: 0,
          selectedBag: null,
        };

        if (!unitMeasure[itemId]) {
          unitMeasure[itemId] = ChoicesOrderUnitMeasure.quantity;
        }
      }
    });

    const totalAmount = Object.values(salesOrderItems).reduce(
      (sum: number, item: any) => sum + (item.amount || 0),
      0
    );

    salesOrders[legalEntityId] = {
      documents: salesOrder.documents || [],
      OVNumber: salesOrder.OVNumber || null,
      totalAmount,
      status: salesOrder.status || 'active',
      salesOrderItems,
    };
  });

  // Ensure all orderItemsToBill items have unit measures defined
  orderItemsToBill.forEach((orderItem) => {
    const itemId = orderItem._id;
    if (validItemIds.has(itemId) && !unitMeasure[itemId]) {
      unitMeasure[itemId] = ChoicesOrderUnitMeasure.quantity;
    }
  });

  const filteredLegalEntities = legalEntities.filter(
    (entity) =>
      salesOrders[entity._id] && Object.keys(salesOrders[entity._id].salesOrderItems).length > 0
  );

  return {
    legalEntities: filteredLegalEntities,
    salesOrders,
    unitMeasure,
  };
};

/**
 * Transforms a flat array of bag allocations into an array of IConsumptionBagAllocation,
 * grouping by consumptionBag and nesting items by orderItem.
 *
 * @param bagAllocations - Flat array of IBagAllocation from currentOrder?.order?.bagAllocations
 * @returns Array of IConsumptionBagAllocation grouped by consumptionBag
 */
export function groupBagAllocationsToConsumptionBagAllocations(
  bagAllocations: IBagAllocation[]
): IConsumptionBagAllocation[] {
  if (!Array.isArray(bagAllocations) || bagAllocations.length === 0) return [];

  const grouped: Record<string, IConsumptionBagAllocation> = {};

  for (const allocation of bagAllocations) {
    const bagId = allocation.consumptionBag;
    if (!grouped[bagId]) {
      grouped[bagId] = {
        consumptionBagId: bagId,
        consumptionOrderItems: {},
        totalAmount: 0,
      };
    }

    grouped[bagId].consumptionOrderItems[allocation.orderItem] = {
      value: allocation.allocatedValue ?? 0,
      amount: allocation.allocatedAmount ?? 0,
      unitMeasure: allocation.unitMeasure as ChoicesOrderUnitMeasure,
    };

    grouped[bagId].totalAmount += allocation.allocatedAmount ?? 0;
  }

  return Object.values(grouped);
}

/**
 * Transforms an array of IConsumptionBagAllocation into ITransformedBagAllocationsData,
 * grouping selectedConsumptionBags and mapping consumptionOrders for form initialization.
 *
 * @param allocations - Array of IConsumptionBagAllocation
 * @param consumptionBags - Array of IConsumptionBagTransformed (for bag info)
 * @param formatter - Intl.NumberFormat instance for formatting (optional)
 * @returns ITransformedBagAllocationsData
 */
export function transformConsumptionBagAllocationsToFormData(
  allocations: IConsumptionBagAllocation[],
  consumptionBags: IConsumptionBagTransformed[]
): ITransformedBagAllocationsData {
  if (!Array.isArray(allocations) || allocations.length === 0) {
    return {
      selectedConsumptionBags: [],
      consumptionOrders: {},
    };
  }

  const bagMap = new Map(consumptionBags.map((bag) => [bag._id, bag]));

  const selectedConsumptionBags: IConsumptionBagTransformed[] = [];
  const consumptionOrders: Record<string, IFormConsumptionOrder> = {};

  allocations.forEach((allocation) => {
    const bagId = allocation.consumptionBagId;
    const bagInfo = bagMap.get(bagId);
    if (!bagInfo) return;

    if (!selectedConsumptionBags.find((b) => b._id === bagId)) {
      selectedConsumptionBags.push(bagInfo);
    }

    consumptionOrders[bagId] = {
      totalAmount: Number(allocation.totalAmount?.toFixed(2)),
      consumptionBag: bagInfo,
      consumptionOrderItems: allocation.consumptionOrderItems,
    };
  });

  return {
    selectedConsumptionBags,
    consumptionOrders,
  };
}

export const transformCurrentBagAllocationsToFormData = (
  currentBagAllocations: IBagAllocation[],
  orderItemsToBill: IOrderItem[],
  consumptionBags: IConsumptionBag[],
  formatter: Intl.NumberFormat
): ITransformedBagAllocationsData => {
  if (!currentBagAllocations || currentBagAllocations.length === 0) {
    return {
      selectedConsumptionBags: [],
      consumptionOrders: {},
    };
  }

  const validOrderItemIds = new Set(orderItemsToBill.map((item) => item._id));
  const consumptionBagMap = new Map(consumptionBags.map((bag) => [bag._id, bag]));

  const allocationsByBag = new Map<string, IBagAllocation[]>();
  const unitMeasureMap = new Map<string, ChoicesOrderUnitMeasure>();

  currentBagAllocations.forEach((allocation) => {
    if (!validOrderItemIds.has(allocation.orderItem)) {
      return;
    }

    const bagId = allocation.consumptionBag;
    if (!allocationsByBag.has(bagId)) {
      allocationsByBag.set(bagId, []);
    }
    allocationsByBag.get(bagId)!.push(allocation);

    const unitMeasure = allocation.unitMeasure as ChoicesOrderUnitMeasure;
    unitMeasureMap.set(allocation.orderItem, unitMeasure);
  });

  const selectedConsumptionBags: IConsumptionBagTransformed[] = [];
  const consumptionOrders: Record<string, IFormConsumptionOrder> = {};

  allocationsByBag.forEach((allocations, bagId) => {
    const consumptionBag = consumptionBagMap.get(bagId);

    if (!consumptionBag) {
      console.warn(`Consumption bag not found for ID: ${bagId}`);
      return;
    }

    const bagTransformed = transformConsumptionBags([consumptionBag], formatter)[0];
    selectedConsumptionBags.push(bagTransformed);

    const consumptionOrderItems: IFormConsumptionOrderItemsMap = {};

    let totalAmount = 0;

    const allocationMap = new Map<string, IBagAllocation>();
    allocations.forEach((allocation) => {
      allocationMap.set(allocation.orderItem, allocation);
    });

    orderItemsToBill.forEach((item) => {
      const allocation = allocationMap.get(item._id);
      if (allocation) {
        consumptionOrderItems[item._id] = {
          value: allocation.allocatedValue,
          amount: allocation.allocatedAmount,
          unitMeasure: allocation.unitMeasure as ChoicesOrderUnitMeasure,
        };
        totalAmount += allocation.allocatedAmount;
      } else {
        consumptionOrderItems[item._id] = {
          value: 0,
          amount: 0,
          unitMeasure: ChoicesOrderUnitMeasure.quantity,
        };
      }
    });

    consumptionOrders[bagId] = {
      totalAmount: Number(totalAmount.toFixed(2)),
      consumptionBag: bagTransformed,
      consumptionOrderItems,
    };
  });

  const unitMeasure: Record<string, ChoicesOrderUnitMeasure> = {};
  unitMeasureMap.forEach((measure, itemId) => {
    unitMeasure[itemId] = measure;
  });

  return {
    selectedConsumptionBags: selectedConsumptionBags,
    consumptionOrders,
  };
};

/**
 * Creates unit measure mapping from items
 * Fallback function when no current sales orders exist
 *
 * @param orderItemsToBill - Array of order items to bill
 * @returns Unit measure mapping with default values
 */
export const createDefaultUnitMeasureMapping = (
  orderItemsToBill: any[]
): Record<string, ChoicesOrderUnitMeasure> => {
  return orderItemsToBill.reduce(
    (acc, item) => {
      acc[item._id] = ChoicesOrderUnitMeasure.quantity;
      return acc;
    },
    {} as Record<string, ChoicesOrderUnitMeasure>
  );
};

/**
 * Validates if there are quantity changes between existing order items and new order data
 * Compares summary?.provision?.quantity values between current and new order items
 * Checks all item types: items, itemsToAddLicenses, itemsToRenewal, itemsToUpgrade
 *
 * @param currentExistsOrder - The existing order from database
 * @param newOrderData - The new order data from form
 * @returns True if there are quantity changes, false otherwise
 */
export const hasQuantityChanges = (currentExistsOrder: any, newOrderData: any): boolean => {
  if (!currentExistsOrder?.items) {
    return false;
  }

  const currentItemsMap = new Map();
  currentExistsOrder.items.forEach((item: any) => {
    if (item._id) {
      currentItemsMap.set(item._id.toString(), item.summary?.provision?.quantity || 0);
    }
  });

  const itemArraysToCheck = [
    newOrderData?.items || [],
    newOrderData?.itemsToAddLicenses || [],
    newOrderData?.itemsToRenewal || [],
    newOrderData?.itemsToUpgrade || [],
  ];

  return itemArraysToCheck.some((itemArray) => {
    return itemArray.some((newItem: any) => {
      if (!newItem._id) return false;

      const itemId = newItem._id.toString();
      const currentQuantity = currentItemsMap.get(itemId);
      const newQuantity = newItem.summary?.provision?.quantity || 0;

      if (currentQuantity !== undefined && currentQuantity !== newQuantity) {
        return true;
      }

      return false;
    });
  });
};

/**
 * Validates if there are custom price changes between existing order items and new order data
 * Compares customPrice.price.price and customPrice.price.finalPrice values
 * This is used to determine if step 2 should be reset due to price modifications
 *
 * @param currentExistsOrder - The existing order from database
 * @param newOrderData - The new order data from form
 * @returns True if there are custom price changes, false otherwise
 */
export const hasCustomPriceChanges = (
  currentExistsOrder: { items?: IOrderItem[] },
  newOrderData: {
    items?: IOrderItem[];
    itemsToAddLicenses?: IOrderItem[];
    itemsToRenewal?: IOrderItem[];
    itemsToUpgrade?: IOrderItem[];
  }
): boolean => {
  if (!currentExistsOrder?.items) {
    return false;
  }

  const currentItemsMap = new Map<string, ICurrentItemPriceData>();
  currentExistsOrder.items.forEach((item: IOrderItem) => {
    if (item._id && item.customPrice?.price) {
      currentItemsMap.set(item._id.toString(), {
        price: item.customPrice.price.price || 0,
        finalPrice: item.customPrice.price.finalPrice || 0,
      });
    }
  });

  const itemArraysToCheck: IOrderItem[][] = [
    newOrderData?.items || [],
    newOrderData?.itemsToAddLicenses || [],
    newOrderData?.itemsToRenewal || [],
    newOrderData?.itemsToUpgrade || [],
  ];

  return itemArraysToCheck.some((itemArray: IOrderItem[]) => {
    return itemArray.some((newItem: IOrderItem) => {
      if (!newItem._id || !newItem.customPrice?.price) return false;

      const itemId = newItem._id.toString();
      const currentPrices = currentItemsMap.get(itemId);

      if (!currentPrices) return false;

      const newPrice = newItem.customPrice.price.price || 0;
      const newFinalPrice = newItem.customPrice.price.finalPrice || 0;

      return currentPrices.price !== newPrice || currentPrices.finalPrice !== newFinalPrice;
    });
  });
};

/**
 * Extracts the product name from an order item with fallback hierarchy
 * Checks multiple possible locations for the product name
 *
 * @param item - The order item containing product information
 * @returns The product name or empty string if not found
 */
export const getProductName = (item: IOrderItem): string => {
  return (
    item?.product?.label ||
    item?.product?.name ||
    item?.sku?.product?.label ||
    item?.sku?.product?.name ||
    (item?.plan as any)?.product?.label ||
    (item?.plan as any)?.product?.name ||
    (item?.plan as any)?.sku?.product?.label ||
    (item?.plan as any)?.sku?.product?.name ||
    ''
  );
};

/**
 * Extracts the SKU name from an order item with fallback hierarchy
 * Checks multiple possible locations for the SKU name
 *
 * @param item - The order item containing SKU information
 * @returns The SKU name or empty string if not found
 */
export const getSkuName = (item: IOrderItem): string => {
  return (
    item?.sku?.name ||
    item?.sku?.label ||
    (item?.plan as any)?.sku?.label ||
    (item?.plan as any)?.sku?.name ||
    ''
  );
};

/**
 * Extracts the partner name from an order item with fallback hierarchy
 * Checks multiple possible locations for the partner name
 *
 * @param item - The order item containing partner information
 * @returns The partner name or empty string if not found
 */
export const getPartnerName = (item: IOrderItem): string => {
  return (
    item?.product?.partner?.name ||
    item?.sku?.product?.partner?.name ||
    (item?.plan as any)?.product?.partner?.name ||
    (item?.plan as any)?.sku?.product?.partner?.name ||
    item?.partner?.name ||
    item?.partner?.label ||
    ''
  );
};

/**
 * Extracts the partner logo URL from an order item with fallback hierarchy
 * Checks multiple possible locations for the partner logo URL
 *
 * @param item - The order item containing partner logo information
 * @returns The partner logo URL or empty string if not found
 */
export const getPartnerLogoUrl = (item: IOrderItem): string => {
  const logoUrl =
    item?.product?.partner?.logo?.url ||
    item?.sku?.product?.partner?.logo?.url ||
    (item?.plan as any)?.product?.partner?.logo?.url ||
    (item?.plan as any)?.sku?.product?.partner?.logo?.url ||
    item?.partner?.logo?.url ||
    '';

  return logoUrl ? decodeURI(logoUrl) : '';
};

/**
 * Extracts the isBillingCloud flag from an order item with fallback hierarchy
 * Checks multiple possible locations for the billing cloud configuration
 *
 * @param item - The order item containing billing cloud information
 * @returns Boolean indicating if the item is billing cloud type, defaults to false if not found
 */
export const getItemIsBillingCloud = (item: IOrderItem): boolean => {
  return (
    item?.product?.isBillingCloud ||
    item?.sku?.product?.isBillingCloud ||
    (item?.plan as any)?.sku?.product?.isBillingCloud ||
    false
  );
};

/**
 * Gets the quantity from an order item
 * Extracts quantity from the provision summary
 *
 * @param item - The order item containing quantity information
 * @returns The quantity value or 0 if not found
 */
export const getItemQuantity = (item: IOrderItem): number => {
  return item.summary?.provision?.quantity || 0;
};

/**
 * Creates a formatted display name for an order item
 * Combines product name and SKU name with proper formatting
 *
 * @param item - The order item
 * @param separator - The separator between product and SKU name (default: ' - ')
 * @returns Formatted display name
 */
export const getItemDisplayName = (item: IOrderItem, separator: string = ' - '): string => {
  const productName = getProductName(item);
  const skuName = getSkuName(item);

  if (productName && skuName) {
    return `${productName}${separator}${skuName}`;
  }

  return productName || skuName || '';
};

/**
 * Formats a numeric value with its appropriate unit based on the unit measure type
 * @param value - Numeric value to format
 * @param unitMeasure - Unit measure type
 * @param formatter - Decimal formatter for currency values
 * @param noDecimalFormatter - Formatter for non-decimal values (quantities)
 * @returns Formatted string with appropriate unit
 */
export const formatValueWithUnit = (
  value: number,
  unitMeasure: ChoicesOrderUnitMeasure,
  formatter: Intl.NumberFormat,
  noDecimalFormatter?: Intl.NumberFormat
): string => {
  switch (unitMeasure) {
    case ChoicesOrderUnitMeasure.amount:
      return formatter.format(value);
    case ChoicesOrderUnitMeasure.percentageAmount:
      return `${formatter.format(value)}%`;
    case ChoicesOrderUnitMeasure.quantity:
    default:
      return noDecimalFormatter ? noDecimalFormatter.format(value) : value.toFixed(2);
  }
};

/**
 * Gets the unit label for a given unit measure
 * @param unitMeasure - Unit measure type
 * @returns Unit label string
 */
export const getUnitLabel = (unitMeasure: ChoicesOrderUnitMeasure): string => {
  switch (unitMeasure) {
    case ChoicesOrderUnitMeasure.amount:
      return '$';
    case ChoicesOrderUnitMeasure.percentageAmount:
      return '%';
    case ChoicesOrderUnitMeasure.quantity:
    default:
      return 'qty';
  }
};

/**
 * Determines if an item or subscription has console access
 * @param item - The order item or subscription object
 * @returns boolean indicating if the item has console access
 */
export const getItemHasConsole = (item: any): boolean => {
  return (
    item?.partner?.hasConsoles ||
    item?.product?.partner?.hasConsoles ||
    item?.sku?.product?.partner?.hasConsoles ||
    item?.sku?.partner?.hasConsoles ||
    (item?.plan as any)?.sku?.product?.partner?.hasConsoles ||
    (item?.plan as any)?.sku?.partner?.hasConsoles ||
    false
  );
};

/**
 * Extracts the plan name from an order item with fallback hierarchy
 * Prioritizes SKU name over product name to get the most specific plan identifier
 *
 * @param item - The order item containing plan information
 * @returns The plan name or 'Undefined' if not found
 */
export const getPlanName = (item: IOrderItem): string => {
  const skuName = getSkuName(item);
  const productName = getProductName(item);

  return skuName || productName || t('Undefined');
};

/**
 * Maps external/raw order item type strings into internal enum values used by UI
 * Avoids hard-coded string checks across components
 */
export const normalizeOrderItemType = (type: any): any => {
  // COMMITTED (backend) -> newResource (UI)
  if (type === PaymentType.COMMITTED) return (ChoicesOrderItemType as any).newResource;
  return type;
};

/**
 * Resolves label to show for an order item type in a centralized place
 */
export const resolveOrderItemTypeLabel = (type: any): string => {
  const normalized = normalizeOrderItemType(type);
  const labelMap = ChoicesOrderItemLabel as Record<string, string>;
  return labelMap[normalized] || (typeof type === 'string' ? type : '');
};

/**
 * Validates if there are billing milestone customization changes between existing order items and new order data
 * Checks for changes in:
 * - customizeBillingMilestones flag at item level
 * - Length of customMilestones arrays (structural changes)
 * - Length of finalCustomMilestones arrays (structural changes)
 * This helps determine if Step 2 configuration needs to be reset due to billing structure changes
 *
 * @param currentExistsOrder - The existing order from database
 * @param newOrderData - The new order data from form
 * @returns True if there are billing milestone customization changes, false otherwise
 */
export const hasBillingMilestoneCustomizationChanges = (
  currentExistsOrder: { items?: IOrderItem[] },
  newOrderData: IOrderDataForMilestoneComparison
): boolean => {
  if (!currentExistsOrder?.items) {
    return false;
  }

  const currentItems = getAllOrderItems(currentExistsOrder as any);
  const newItems = getAllOrderItems(newOrderData as any);

  // Create maps for comparison
  const currentItemsMap = new Map<string, ICurrentItemMilestoneData>();
  const newItemsMap = new Map<string, ICurrentItemMilestoneData>();

  currentItems.forEach((item: IOrderItem) => {
    if (item._id) {
      currentItemsMap.set(item._id.toString(), {
        customizeBillingMilestones: item.customizeBillingMilestones || false,
        customMilestonesLength: item.customMilestones?.length || 0,
        finalCustomMilestonesLength: item.finalCustomMilestones?.length || 0,
      });
    }
  });

  newItems.forEach((item: IOrderItem) => {
    if (item._id) {
      newItemsMap.set(item._id.toString(), {
        customizeBillingMilestones: item.customizeBillingMilestones || false,
        customMilestonesLength: item.customMilestones?.length || 0,
        finalCustomMilestonesLength: item.finalCustomMilestones?.length || 0,
      });
    }
  });

  for (const [itemId, currentData] of currentItemsMap) {
    const newData = newItemsMap.get(itemId);
    if (!newData) continue;

    if (currentData.customizeBillingMilestones !== newData.customizeBillingMilestones) {
      return true;
    }

    if (currentData.customizeBillingMilestones || newData.customizeBillingMilestones) {
      if (
        currentData.customMilestonesLength !== newData.customMilestonesLength ||
        currentData.finalCustomMilestonesLength !== newData.finalCustomMilestonesLength
      ) {
        return true;
      }
    }
  }

  return false;
};
